import { ArticleUtils } from '../../../util/article-utils';
import { ArticleManager } from '../../../manager/article.manager';
import { ProjectModel } from '@project/model/project.model';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { BusbarArticleConnectProcessor } from '@project/components/article/processor/prepare/connect/busbar-article-connect.processor';
import { ArticleUsageUtils } from '../../../util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '../../../constants/article-usage-info-property-def';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ArticleContainerPropertyDef } from '../../../constants/article-container-property-def';
import { Busbar } from './replace-busbar-data';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageVO, ArticleVO } from '../../../model/vo/article.vo';

export class ReplaceBusbarArticleCreator {
  public constructor(private projectModel: ProjectModel,
                     private articleManager: ArticleManager,
                     private busbarArticleConnectProcessor: BusbarArticleConnectProcessor,
                     private projectArticleService: ProjectArticleService,
                     private connectionInfoService: ConnectionInfoService) {
  }

  public async createBusbarArticles(busbars: Busbar[]): Promise<ArticleContainer[]> {
    const result: ArticleContainer[] = [];
    for (const busbar of busbars) {
      const originalUsage: ArticleUsageVO = this.projectModel.getArticleUsage(busbar.originalUsageId);
      if (!originalUsage) {
        // create new busbar article if original article was already removed
        result.push(...await this.createBusbarArticle(busbar));
      }
    }
    return result;
  }

  private async createBusbarArticle(busbar: Busbar): Promise<ArticleContainer[]> {
    const result: ArticleContainer[] = [];

    const defaultArticle: ArticleVO | undefined = this.articleManager.getCachedArticle(busbar.articleId, busbar.articleVersion);
    if (!defaultArticle) {
      return result;
    }

    const articleWrappers: ArticleContainersWrapper[] = this.busbarArticleConnectProcessor
      .preProcessWithoutRefresh(defaultArticle, busbar.getAcceptingConnections(this.connectionInfoService));
    for (const articleWrapper of articleWrappers) {
      this.prepareBusbarArticleWrapper(defaultArticle, busbar, articleWrapper);
      await articleWrapper.refreshWrapper();

      this.connectBusbarToBaseArticle(defaultArticle, articleWrapper);

      result.push(...articleWrapper.articleContainers);
    }

    return result;
  }

  private prepareBusbarArticleWrapper(defaultArticle: ArticleVO, busbar: Busbar, articleWrapper: ArticleContainersWrapper) {
    const articleUsage: ArticleUsageVO = this.projectArticleService.createNewArticleUsage(defaultArticle.identity, defaultArticle.version);
    busbar.newUsageIds.push(articleUsage.id);
    ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_ADDITIONAL_LENGTH_START, busbar.additionalLengthStart);
    ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_ADDITIONAL_LENGTH_END, busbar.additionalLengthEnd);

    if (busbar.additionalLengthStart) {
      const rectangle: Rectangle3D = articleWrapper.properties.getValue(ArticleContainerPropertyDef.RECTANGLE);
      rectangle.point1.x += busbar.additionalLengthStart;
      rectangle.point4.x += busbar.additionalLengthStart;
      rectangle.point2.x += busbar.additionalLengthStart;
      rectangle.point3.x += busbar.additionalLengthStart;
    }

    articleWrapper.articleUsageID = articleUsage.id;
  }

  private connectBusbarToBaseArticle(defaultArticle: ArticleVO, articleWrapper: ArticleContainersWrapper) {
    const givingArea = ArticleUtils.findFirstConnectionArea(defaultArticle.metaElements, ConnectionAreaVO.SIDE_GIVING);
    if (givingArea) {
      this.busbarArticleConnectProcessor.process(articleWrapper.articleContainers, articleWrapper.baseArticleContainer, [givingArea], 0);
    }
  }
}
